<template>
	<div class="gap-2 items-center text-lg font-semibold">
		<div class="shrink-0">
			<a @click="navigateToAllLessonPlans" class="hover:underline cursor-pointer">
				{{ $t("All lesson plans") }}
			</a>
		</div>
		<div v-show="selectedCollection" class="shrink-0 relative">
			<ChevronRightIcon class="w-3 h-3 text-base-content/60" />
			<div class="absolute -top-6 left-2 z-10 w-72">
				<LessonPlanCollectionEditor
					v-if="showEditCollectionModal"
					:focus="showEditCollectionModal"
					context="library"
					:collection="selectedCollection"
					@requestUnmount="showEditCollectionModal = false"
					@updated="showEditCollectionModal = false"
				/>
			</div>
		</div>
		<div v-if="selectedCollection" class="min-w-0 max-w-sm flex items-center">
			<p class="block truncate">{{ showEditCollectionModal ? '' : selectedCollection.name }}</p>
			<IconSharedLessonPlanCollection v-if="selectedCollection.sharing" :collection="selectedCollection" />
		</div>
		<div v-show="selectedCollection && !readOnly" class="shrink-0">
			<Dropdown position="bottom-end" ref="ellipsisDropdown">
				<template #trigger>
					<button type="button" class="btn btn-ghost btn-circle btn-sm shrink-0">
						<EllipsisVerticalIcon class="w-5 h-5" />
					</button>
				</template>
				<template #content>
					<DropdownMenu class="w-48">
						<DropdownMenuItem>
							<a @click="openEditCollectionModal">
								<PencilIcon class="w-4 h-4" />
								{{ $t("Edit") }}
							</a>
						</DropdownMenuItem>
						<DropdownMenuItem>
							<a @click="openShareCollectionModal">
								<!-- Phospor icon share-network -->
								<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 256 256">
									<path d="M176,160a39.89,39.89,0,0,0-28.62,12.09l-46.1-29.63a39.8,39.8,0,0,0,0-28.92l46.1-29.63a40,40,0,1,0-8.66-13.45l-46.1,29.63a40,40,0,1,0,0,55.82l46.1,29.63A40,40,0,1,0,176,160Zm0-128a24,24,0,1,1-24,24A24,24,0,0,1,176,32ZM64,152a24,24,0,1,1,24-24A24,24,0,0,1,64,152Zm112,72a24,24,0,1,1,24-24A24,24,0,0,1,176,224Z"></path>
								</svg>
								{{ $t("Share") }}
							</a>
						</DropdownMenuItem>
						<DropdownMenuItem v-if="emptyCollection" :disabled="true">
							<a>
								<ArrowUturnRightIcon class="w-4 h-4" />
								{{ $t("Transfer lesson plans") }}
							</a>
						</DropdownMenuItem>
						<DropdownMenuItem v-if="!emptyCollection">
							<a @click="openTransferModal">
								<ArrowUturnRightIcon class="w-4 h-4" />
								{{ $t("Transfer lesson plans") }}
							</a>
						</DropdownMenuItem>
						<DropdownMenuItem>
							<a @click="openDeleteCollectionModal" class="hover:text-danger">
								<TrashIcon class="w-4 h-4" />
								{{ $t("Remove") }}
							</a>
						</DropdownMenuItem>
					</DropdownMenu>
				</template>
			</Dropdown>
		</div>

		<LessonPlanCollectionShareModal
			v-if="showShareCollectionModal && selectedCollection"
			:collection="selectedCollection"
			@sharingChanged="onChangedCollectionSharing"
			@safeToUnmount="showShareCollectionModal = false"
		/>

		<LessonPlanCollectionTransferModal
			v-if="showTransferModal"
			:sourceCollection="delayedSelectedCollectionMirror"
			@transferred="onSuccessfulCollectionTransfer"
			@safeToUnmount="showTransferModal = false"
		/>

		<LessonPlanCollectionDeleteModal
			v-if="showDeleteCollectionModal"
			:collection="delayedSelectedCollectionMirror"
			@deleted="onSuccessfulCollectionDelete"
			@safeToUnmount="showDeleteCollectionModal = false"
		/>
	</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";

import { ChevronRightIcon, EllipsisVerticalIcon, PencilIcon, TrashIcon, ArrowUturnRightIcon } from "@heroicons/vue/24/outline";
import Dropdown from "@/components/ui/Dropdown.vue";
import DropdownMenu from "@/components/ui/DropdownMenu.vue";
import DropdownMenuItem from "@/components/ui/DropdownMenuItem.vue";
import LessonPlanCollectionEditor from "@/components/LessonPlanCollectionEditor.vue";
import LessonPlanCollectionDeleteModal from "@/components/LessonPlanCollectionDeleteModal.vue";
import LessonPlanCollectionTransferModal from "@/components/LessonPlanCollectionTransferModal.vue";
import LessonPlanCollectionShareModal from "@/components/LessonPlanCollectionShareModal.vue";
import IconSharedLessonPlanCollection from "@/components/IconSharedLessonPlanCollection.vue";

export default {
	name: "LessonPlanMyLibraryListHeader",

	props: {
		selectedCollection: {
			type: Object as () => LessonPlanCollection | null,
			default: null,
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
		resetModals: {
			type: Boolean,
			default: false,
		},
	},

	components: {
		ChevronRightIcon, EllipsisVerticalIcon, PencilIcon, TrashIcon, ArrowUturnRightIcon,
		Dropdown, DropdownMenu, DropdownMenuItem,
		LessonPlanCollectionEditor,
		LessonPlanCollectionDeleteModal,
		LessonPlanCollectionTransferModal,
		LessonPlanCollectionShareModal,
		IconSharedLessonPlanCollection,
	},

	data() {
		return {
			showEditCollectionModal: false,
			showTransferModal: false,
			showDeleteCollectionModal: false,
			showShareCollectionModal: false,
			// This is a mirror of the selected collection that is updated with a small delay. This is needed to prevent the modals from closing immediately
			// when the collection they are bound to (v-if) is deleted.
			delayedSelectedCollectionMirror: null as LessonPlanCollection | null,
		}
	},

	computed: {
		...mapStores(useLessonPlansStore, useLessonPlanCollectionsStore),

		emptyCollection(): boolean {
			return (this.selectedCollection && (this.selectedCollection.planCount ?? 0) === 0) ? true : false;
		},
	},

	mounted() {
		this.delayedSelectedCollectionMirror = this.selectedCollection;
	},

	watch: {
		resetModals(newValue) {
			if (newValue) {
				this.closeAllModals();
			}
		},
		selectedCollection(newValue, oldValue) {
			if (newValue?.id !== oldValue?.id) {
				this.closeAllModals();
			}
			// update the mirror with a delay to prevent modals from closing immediately when switching between collections (since the modals are bound to the selected collection)
			setTimeout(() => {
				this.delayedSelectedCollectionMirror = newValue;
			}, 300);
		}
	},

	methods: {

		navigateToAllLessonPlans() {
			this.lessonPlansStore.resetFilter();
			this.$router.push({ name: 'lesson-plan-library.my-library' });
		},

		closeAllModals() {
			this.showEditCollectionModal = false;
			// Don't close the 'delete' or 'transfer' modal here, since they will be closed automatically after the respective actions have been completed
			// and by closing them prematurely we might interrupt event handling (like the 'deleted' event emitted by the delete modal).
			// These modals also block the UI with a backdrop, so no other action requiring to close them can be performed while they are open anyway.
			// this.showTransferModal = false;
			// this.showDeleteCollectionModal = false;
			// this.showShareCollectionModal = false;
			this.closeCollectionDropDownMenu();
		},

		closeCollectionDropDownMenu() {
			const dropdown = this.$refs.ellipsisDropdown as any;
			if (dropdown && dropdown.close) {
				dropdown.close();
			}
		},

		openEditCollectionModal() {
			if (!this.selectedCollection) return;
			this.closeCollectionDropDownMenu();
			this.showEditCollectionModal = true;
		},

		openShareCollectionModal() {
			if (!this.selectedCollection) return;
			this.closeCollectionDropDownMenu();
			this.showShareCollectionModal = true;
		},

		openTransferModal() {
			if (!this.selectedCollection || this.emptyCollection) return;
			this.closeCollectionDropDownMenu();
			this.showTransferModal = true;
		},

		openDeleteCollectionModal() {
			if (!this.selectedCollection) return;
			this.closeCollectionDropDownMenu();
			this.showDeleteCollectionModal = true;
		},

		onSuccessfulCollectionDelete() {
			this.showDeleteCollectionModal = false;
			this.navigateToAllLessonPlans();
			this.$notify({ text: this.$t('Collection deleted successfully'), type: 'success' });
		},

		onSuccessfulCollectionTransfer(
			{ sourceCollection, destinationCollection, removeSource }: { sourceCollection: LessonPlanCollection, destinationCollection: LessonPlanCollection, removeSource: boolean }
		) {
			// navigate to destination collection if source was removed
			if(removeSource) {
				this.$router.push({ name: 'lesson-plan-library.my-library', params: { collectionId: destinationCollection.id } });
			} else {
				// ... otherwise stay on the same collection which now is empty
				// ... but reload the lesson plans since we're watching an empty collection now, which should be reflected in the store
				this.lessonPlansStore.load();
			}

			this.$notify({ text: this.$t('Lesson plans transferred successfully'), type: 'success' });
		},

		onChangedCollectionSharing(newSharingState: null | string) {
			if(newSharingState === null) {
				this.$notify({ text: this.$t('Collection no longer shared'), type: 'success' });
			} else {
				this.$notify({ text: this.$t('Collection shared successfully'), type: 'success' });
			}
		}
	}
}
</script>
